import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import InlineLoader from '../components/Loader/InlineLoader';
import PatientImage from '../components/Patient/PatientImage';
import PaymentMethodBadge from '../components/Order/PaymentMethodBadge';
import Table from '../components/Listing/Table';
import FabGroup from '../components/FabGroup';
import Modal from '../components/Modal/Modal';
import { Document, Page, pdfjs } from 'react-pdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileInvoice, faCircleCheck, faClock, faXmark,
    faFileAlt, faCoins, faMoneyBillTransfer,
    faInfoCircle, faUser, faUserSlash, faPills, faPrint, faDownload,
    faArrowUpRightFromSquare,
    faCalendarDays, faTachometerAlt,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../api';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import usePaymentColumns from '../hooks/paymentColumns';
import useRecordNeighbors from '../hooks/useRecordNeighbors';
import RecordNav from '../components/RecordNav/RecordNav';
import NavTab from '../components/NavTab/NavTab';
import './InvoiceDetails.scss';

import 'react-pdf/dist/Page/TextLayer.css';
import { formatListingDate, formatRelativeDate } from '../utils/FormatDate';

// invoice sheet: brand-blue icon tiles, like the product / order sheets
const SECTION_ICON_STYLE = { background: '#eff6ff', color: '#2563eb' };

// tab ↔ URL slug (/invoices/:id/items…), like the sale sheet
const TAB_SLUGS = {
    invoiceItems: 'items',
    invoicePayments: 'payments',
};

const SLUG_TO_TAB = Object.fromEntries(
    Object.entries(TAB_SLUGS).map(([tabId, slug]) => [slug, tabId])
);

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const fmt = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const getStatusMap = (t) => ({
    PAID:            { label: t('invoice_details.status.paid'),            cls: 'inv-badge--green',   icon: faCircleCheck },
    ISSUED:          { label: t('invoice_details.status.issued'),          cls: 'inv-badge--blue',    icon: faFileAlt     },
    PARTIALLY_PAID:  { label: t('invoice_details.status.partially_paid'),  cls: 'inv-badge--amber',   icon: faClock       },
    DRAFT:           { label: t('invoice_details.status.draft'),           cls: 'inv-badge--neutral', icon: faFileAlt     },
    CANCELLED:       { label: t('invoice_details.status.cancelled'),       cls: 'inv-badge--red',     icon: faXmark       },
});

const FieldRow = ({ label, children }) => (
    <div className="inv-field-row">
        <span className="inv-field-label">{label}</span>
        <span className="inv-field-value">{children}</span>
    </div>
);

export default function InvoiceDetails() {
    const { t } = useTranslation();
    const { id, tab } = useParams();
    const navigate = useNavigate();
    const activeTab = SLUG_TO_TAB[tab] ?? 'invoiceOverview';
    const handleTabChange = (tabId) => {
        const slug = TAB_SLUGS[tabId];
        navigate(slug ? `/invoices/${id}/${slug}` : `/invoices/${id}`);
    };
    // keeps the current tab when moving to the previous / next invoice
    const invoicePath = useCallback((targetId) => `/invoices/${targetId}${tab ? `/${tab}` : ''}`, [tab]);
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const { setTitle } = usePageTitle();
    const { setBreadcrumb } = useBreadcrumb();

    const [pdfData,      setPdfData]      = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [numPages,     setNumPages]     = useState(null);
    const [pageNumber,   setPageNumber]   = useState(1);

    // Same shared columns as the main /payments listing — invoiceId/patientId are
    // dropped since every row here already belongs to this one invoice and patient.
    // Declared before the loading/not-found early returns below so the hook still
    // runs on every render (Rules of Hooks).
    // Previous / next invoice (header arrows + ← → keys)
    const { neighbors, goTo } = useRecordNeighbors(`/api/v1/invoice/${id}/neighbors`, invoicePath);

    const paymentColumns = usePaymentColumns({
        setPdfData,
        setShowPdfModal,
        setPageNumber,
        excludeColumns: ['invoiceId', 'patientId'],
    });

    const handleViewPdf = () => {
        axios.get(`${API_BASE_URL}/api/v1/invoice/${id}/download`, {
            withCredentials: true,
            responseType: 'blob',
        })
            .then((response) => {
                const fileURL = URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );
                setPdfData(fileURL);
                setShowPdfModal(true);
                setPageNumber(1);
            })
            .catch((error) => console.error('Erreur PDF :', error));
    };

    const handleDownloadPdf = () => {
        axios.get(`${API_BASE_URL}/api/v1/invoice/${id}/download`, {
            withCredentials: true,
            responseType: 'blob',
        })
            .then((response) => {
                const fileURL = URL.createObjectURL(
                    new Blob([response.data], { type: 'application/pdf' })
                );
                const link = document.createElement('a');
                link.href = fileURL;
                link.download = `facture-${invoice?.invoiceNumber ?? id}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(fileURL);
            })
            .catch((error) => console.error('Erreur téléchargement :', error));
    };

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/invoice/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setInvoice(data);
                setTitle(data.invoiceNumber);
                document.title = data.invoiceNumber;
                setBreadcrumb([
                    { label: t('Invoices'), url: '/invoices' },
                    { label: data.invoiceNumber },
                ]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id, t]);

    if (loading) return <InlineLoader loadingMessage={t('Loading...')} />;
    if (!invoice) return <div className="page-content text-muted p-4">{t('invoice_details.not_found')}</div>;

    const STATUS_MAP = getStatusMap(t);
    const status  = STATUS_MAP[invoice.status] ?? { label: invoice.status, cls: 'inv-badge--neutral', icon: faFileAlt };
    const items    = (invoice.items   ?? []).map((item, i) => ({
        ...item,
        _index:     i + 1,
        _lineTotal: (item.unitPrice ?? 0) * (item.quantity ?? 1),
    }));
    const payments = invoice.payments ?? [];

    const invoiceDate = invoice.invoiceDate
        ? new Date(invoice.invoiceDate).toLocaleDateString('fr-MA', { day: '2-digit', month: 'long', year: 'numeric' })
        : '—';

    const fabButtons = [
        {
            id: 'print',
            label: t('invoice_details.action.print'),
            icon: faPrint,
            onClick: handleViewPdf,
        },
        {
            id: 'download',
            label: t('invoice_details.action.download'),
            icon: faDownload,
            onClick: handleDownloadPdf,
        },
    ];

    return (
        <div className="page-content inv-detail">

            <FabGroup buttons={fabButtons} />

            {/* ── Header card ── */}
            <div className="inv-header-card inv-header-card--hero">
                <div className="inv-header-body">
                    <div className="inv-avatar">
                        <FontAwesomeIcon icon={faFileInvoice} />
                    </div>
                    <div>
                        <p className="inv-number">{invoice.invoiceNumber}</p>
                        <p className="inv-date">{invoiceDate}</p>
                        <span className={`inv-badge ${status.cls}`}>
                            <FontAwesomeIcon icon={status.icon} />
                            {status.label}
                        </span>
                    </div>
                    <RecordNav neighbors={neighbors} onNavigate={goTo} noun="Facture" feminine />
                </div>

                {/* ── Totaux ── */}
                <div className="inv-totals-grid">
                    {/* date of the invoice: day + time, relative age underneath */}
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCalendarDays} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Date')}</div>
                            <div className="inv-total-value inv-total-value--date">{formatListingDate(invoice.invoiceDate)}</div>
                            <div className="inv-total-sub">{formatRelativeDate(invoice.invoiceDate)}</div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faFileInvoice} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Total')}</div>
                            <div className="inv-total-value">
                                {fmt(invoice.totalAmount)} <small>dhs</small>
                            </div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faPills} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('invoice_details.section.items')}</div>
                            <div className="inv-total-value">{items.length}</div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCoins} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Payments')}</div>
                            <div className="inv-total-value">{payments.length}</div>
                        </div>
                    </div>
                    <div className="inv-total-card">
                        <span className="inv-total-card__icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faMoneyBillTransfer} />
                        </span>
                        <div>
                            <div className="inv-total-label">{t('Method')}</div>
                            <div style={{ marginTop: 4 }}>
                                {payments[0]?.paymentMethod
                                    ? <PaymentMethodBadge method={payments[0].paymentMethod} />
                                    : <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Left menu, same as the sale sheet (OrderDetails) ── */}
            <NavTab
                activeTab={activeTab}
                onTabChange={handleTabChange}
                className="navtab--narrow"
                menuItems={[
                    {
                        id: 'invoiceOverview',
                        icon: faTachometerAlt,
                        label: t('order_details.tab.overview'),
                        component: (
            <div className="inv-col-layout">

                <div className="inv-section-card">
                    <div className="inv-section-title">
                        <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                        {t('Information')}
                    </div>
                    <FieldRow label={t('invoice_details.field.invoice_number')}>{invoice.invoiceNumber}</FieldRow>
                    <FieldRow label={t('Date')}>{invoiceDate}</FieldRow>
                    {invoice.orderNumber && (
                        <FieldRow label={t('Order')}>
                            <NavLink
                                to={`/order/${invoice.orderId ?? ''}`}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 28, height: 28, borderRadius: 8,
                                    background: '#f3f4f6', color: '#6b7280',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
                                data-tooltip-id="tooltip"
                                data-tooltip-content={invoice.orderNumber}
                            >
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
                            </NavLink>
                        </FieldRow>
                    )}
                    <FieldRow label={t('Status')}>
                        <span className={`inv-badge ${status.cls}`}>
                            <FontAwesomeIcon icon={status.icon} /> {status.label}
                        </span>
                    </FieldRow>
                    <FieldRow label={t('Total')}>{fmt(invoice.totalAmount)} <span className="inv-currency">dhs</span></FieldRow>
                </div>

                {invoice.patient ? (
                    <div className="inv-section-card">
                        <div className="inv-section-title">
                            <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                            {t('Patient')}
                        </div>
                        <NavLink to={`/patient/${invoice.patient.id}`} className="inv-patient-card">
                            <PatientImage patient={invoice.patient} />
                            <div>
                                <p className="inv-patient-name">
                                    {invoice.patient.firstName} {invoice.patient.lastName}
                                </p>
                                <p className="inv-patient-sub">{invoice.patient.phone ?? '—'}</p>
                            </div>
                        </NavLink>
                    </div>
                ) : (
                    <div className="inv-section-card">
                        <div className="inv-section-title">
                            <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                                <FontAwesomeIcon icon={faUser} />
                            </span>
                            {t('Patient')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1.25rem', color: '#9ca3af' }}>
                            <FontAwesomeIcon icon={faUserSlash} style={{ fontSize: 14 }} />
                            <span style={{ fontSize: 13 }}>{t('Anonymous Visitor')}</span>
                        </div>
                    </div>
                )}
            </div>
                        ),
                    },
                    {
                        id: 'invoiceItems',
                        icon: faPills,
                        label: t('invoice_details.section.items'),
                        count: items.length,
                        component: (
            <div className="inv-section-card">
                <div className="inv-section-title">
                    <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                        <FontAwesomeIcon icon={faPills} />
                    </span>
                    {t('invoice_details.section.items')}
                    <span className="inv-count-badge">{items.length}</span>
                </div>
                <Table
                    mode="client"
                    data={items}
                    columns={[
                        {
                            Header: '#',
                            accessor: '_index',
                            className: 'text-center',
                            Cell: (row) => <span style={{ color: '#9ca3af', fontSize: 12 }}>{row._index}</span>,
                        },
                        {
                            Header: t('invoice_details.column.product_service'),
                            accessor: 'itemName',
                            Cell: (row) => {
                                const sub = [row.presentation, row.dosage].filter(Boolean).join(' · ');
                                return (
                                    <div>
                                        <span style={{ fontWeight: 500, color: '#111827' }}>{row.itemName}</span>
                                        {sub && <span className="cell-sub" style={{ display: 'block' }}>{sub}</span>}
                                        {row.laboratoryName && (
                                            <span className="cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, color: '#374151' }}>
                                                Par <LaboratoryLogo name={row.laboratoryName} size={16} />
                                                {row.laboratoryName}
                                            </span>
                                        )}
                                    </div>
                                );
                            },
                        },
                        {
                            Header: t('invoice_details.column.qty'),
                            accessor: 'quantity',
                            className: 'text-center',
                        },
                        {
                            Header: t('invoice_details.column.unit_price'),
                            accessor: 'unitPrice',
                            className: 'text-end',
                            Cell: (row) => <span>{fmt(row.unitPrice)} <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span></span>,
                        },
                        {
                            Header: t('Total'),
                            accessor: '_lineTotal',
                            className: 'text-end',
                            Cell: (row) => <span style={{ fontWeight: 600 }}>{fmt(row._lineTotal)} <span style={{ fontSize: 11, color: '#9ca3af' }}>dhs</span></span>,
                        },
                    ]}
                />
            </div>
                        ),
                    },
                    {
                        id: 'invoicePayments',
                        icon: faCoins,
                        label: t('Payments'),
                        count: payments.length,
                        component: (
                <div className="inv-section-card">
                    <div className="inv-section-title">
                        <span className="inv-section-icon" style={SECTION_ICON_STYLE}>
                            <FontAwesomeIcon icon={faCoins} />
                        </span>
                        {t('Payments')}
                        <span className="inv-count-badge">{payments.length}</span>
                    </div>
                    <Table
                        mode="client"
                        data={payments}
                        columns={paymentColumns}
                        multiSelect={true}
                    />
                </div>
                        ),
                    },
                ]}
            />

            {/* ── Invoice PDF modal ── */}
            <Modal
                title={t('Invoice')}
                show={showPdfModal}
                onClose={() => { setShowPdfModal(false); setPdfData(null); }}
                classSize="modal-lg"
            >
                {pdfData ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Document file={pdfData} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                                <Page pageNumber={pageNumber} width={680} />
                            </Document>
                        </div>
                        {numPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <button className="btn btn-outline-primary btn-sm"
                                    disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>
                                    {t('Previous')}
                                </button>
                                <span className="text-muted small">{t('pdf_viewer.page_indicator', { page: pageNumber, total: numPages })}</span>
                                <button className="btn btn-outline-primary btn-sm"
                                    disabled={pageNumber >= numPages} onClick={() => setPageNumber(pageNumber + 1)}>
                                    {t('Next')}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-4 text-muted">{t('invoice_details.pdf_loading')}</div>
                )}
            </Modal>
        </div>
    );
}
